// « Taps Sextant » — the view rules of PMS-LEDGER-TAP-VIEW-BRIEF (T2), kept pure so
// they can be tested without React or the database.
//
// Operator decisions (2026-10-10): every tap is shown; nothing is excluded. The switch
// « Afficher les rapprochés » hides matched taps by default and shows them for audit.
// The period is the tap date (Paris), 3 days by default, 31 days at most.

import type { CardTap, CardTapState, IsoDate } from '@lemontaigu/pms-core';

export const TAP_STATE_FR: Record<CardTapState, string> = {
  rattache: 'rattaché',
  hors_vega: 'hors Vega (Lightspeed)',
  en_attente_import: 'en attente d’import',
  non_rattache: 'non rattaché',
};

export const MAX_RANGE_DAYS = 31;
export const DEFAULT_RANGE_DAYS = 3;

/** The two audit switches (operator): each shows one kind of matched tap. Both off = only
 *  the taps that still need a look. */
export interface AuditShow {
  /** Taps lettered to a Vega payment (« rattaché »). */
  vega: boolean;
  /** Taps lettered to a Lightspeed sale (« hors Vega »). */
  ls: boolean;
}

/** The taps to show, in server order. Taps that need a look always show; matched taps show
 *  only when their switch is on. */
export function visibleTaps(taps: readonly CardTap[], show: AuditShow): CardTap[] {
  return taps.filter((t) => {
    if (t.state === 'rattache') return show.vega;
    if (t.state === 'hors_vega') return show.ls;
    return true;
  });
}

/** The shift of an ISO day, in calendar days (UTC arithmetic: a Paris date has no DST issue here). */
export function addDays(day: IsoDate, n: number): IsoDate {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10) as IsoDate;
}

/** The default period: the last three Paris days, today included. */
export function defaultRange(today: IsoDate): { from: IsoDate; to: IsoDate } {
  return { from: addDays(today, -(DEFAULT_RANGE_DAYS - 1)), to: today };
}

/** Null when the range can be asked; otherwise the French reason (the server refuses the same). */
export function rangeError(from: IsoDate, to: IsoDate): string | null {
  if (to < from) return 'La fin de période précède le début.';
  const days = (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000;
  if (days + 1 > MAX_RANGE_DAYS) return `Période limitée à ${MAX_RANGE_DAYS} jours.`;
  return null;
}

/** A reading older than this is shown as possibly stale: the reading runs hourly, so a
 *  three-hour gap means the scheduled task did not run (not that Vega's day changed). */
export const STALE_READING_HOURS = 3;

export function isStaleReading(readAtIso: string, nowMs: number): boolean {
  const readAt = Date.parse(readAtIso);
  if (Number.isNaN(readAt)) return true;
  return nowMs - readAt > STALE_READING_HOURS * 3_600_000;
}

// ── Vente manquante (« Passage ») — the declaration form ─────────────────────────────────────────

/** A tap under this amount (TTC) defaults to the bar (20 %); otherwise to the restaurant. Operator rule. */
export const BAR_DEFAULT_BELOW_CENTS = 2000;

/** The default split of a tap's amount: all to the bar if strictly under 20 €, else all to the restaurant. */
export function defaultSplit(totalCents: number): { barCents: number; restoCents: number } {
  return totalCents < BAR_DEFAULT_BELOW_CENTS
    ? { barCents: totalCents, restoCents: 0 }
    : { barCents: 0, restoCents: totalCents };
}

/** The bar amount that completes a restaurant amount: the two always add up to the tap (never refused). */
export function barFromResto(totalCents: number, restoCents: number): number {
  return Math.max(0, totalCents - Math.min(Math.max(0, restoCents), totalCents));
}

/** The restaurant amount that completes a bar amount. */
export function restoFromBar(totalCents: number, barCents: number): number {
  return Math.max(0, totalCents - Math.min(Math.max(0, barCents), totalCents));
}

/** « 5,00 » or « 5.00 » to cents; null when not a euro amount (negative, or not a number). */
export function parseEurosToCents(text: string): number | null {
  const t = text.trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  return Math.round(Number(t) * 100);
}

/** Cents to « 5,00 » (the French form the operator types). */
export function centsToEuros(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

/** A tap is declarable when nothing matches it and no declaration exists yet. */
export function isDeclarable(tap: CardTap): boolean {
  return tap.state === 'non_rattache' && !tap.detail.startsWith('vente déclarée');
}

const PARIS_TIME = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  hour: '2-digit',
  minute: '2-digit',
});
const PARIS_DAY = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/** Clock time of an instant, in Paris (the terminal and Vega both keep Paris time). */
export function parisTime(iso: string): string {
  return PARIS_TIME.format(new Date(iso));
}

/** « 10/10/2026 » from an ISO day. */
export function frDay(day: IsoDate): string {
  return PARIS_DAY.format(new Date(`${day}T12:00:00Z`));
}

/** « 18:39 » for the reading, with its date if it is not today. */
export function readingLabel(readAtIso: string, todayIso: IsoDate): string {
  const readDay = PARIS_DAY.format(new Date(readAtIso));
  const time = parisTime(readAtIso);
  return readDay === frDay(todayIso) ? `lu à ${time}` : `lu le ${readDay} à ${time}`;
}
